import React, { createContext, useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import type { Language } from '../types';
import { LanguageUtils } from '../../lib/languageUtils';

type Translations = Record<string, string>;

interface LanguageContextType {
  language: Language;
  setLanguage: (language: Language) => void;
  t: (key: string, options?: string | Record<string, string | number>) => string;
  loading: boolean;
}

export const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get language from URL parameter, falling back to the shared default.
  const getLanguageFromURL = useCallback((): Language => {
    const langParam = params.lang;
    return langParam && LanguageUtils.isValidLanguage(langParam)
      ? langParam
      : LanguageUtils.getDefaultLanguage();
  }, [params.lang]);

  const [language, setLanguageState] = useState<Language>(() => getLanguageFromURL());
  const [translations, setTranslations] = useState<Translations>({});
  const [loading, setLoading] = useState(true);
  const hasLoadedTranslations = useRef(false);

  // Custom setLanguage function that navigates to the new language URL
  const setLanguage = useCallback((newLanguage: Language) => {
    setLanguageState(newLanguage);
    if (typeof window !== 'undefined') {
      localStorage.setItem('samadhi-language', newLanguage);
    }
    
    // Navigate to the same path but with the new language
    const currentPath = location.pathname;
    const pathWithoutLang = currentPath.replace(/^\/[a-z]{2}/, '');
    const newPath = `/${newLanguage}${pathWithoutLang}`;
    navigate(newPath, { replace: true });
  }, [navigate, location.pathname]);

  // Update language when URL changes
  useEffect(() => {
    const urlLanguage = getLanguageFromURL();
    if (urlLanguage !== language) {
      setLanguageState(urlLanguage);
      if (typeof window !== 'undefined') {
        localStorage.setItem('samadhi-language', urlLanguage);
      }
    }
  }, [params.lang, language, getLanguageFromURL]);

  useEffect(() => {
    let cancelled = false;
    const fetchTranslations = async () => {
        setLoading(true);
        try {
            const response = await fetch(`/translations/${language}.json`);
            if (!response.ok) {
                throw new Error(`Could not load ${language} translations`);
            }
            const data = await response.json();
            if (!cancelled) {
              setTranslations(data);
            }
        } catch (error) {
            if (!cancelled) {
                console.error(error);
                // Fallback to English if loading fails
                if (language !== 'en') {
                    setLanguage('en');
                }
            }
        } finally {
            if (!cancelled) {
                hasLoadedTranslations.current = true;
                setLoading(false);
            }
        }
    };
    fetchTranslations();
    return () => {
      cancelled = true;
    };
  }, [language, setLanguage]);

  // WCAG 2.1 3.1.1 Language of Page (Level A). index.html hardcodes
  // lang="en" and nothing updated it, so every Sinhala page still declared
  // itself English — a screen reader then applies English pronunciation rules
  // to Sinhala text, which is the failure mode this criterion exists for. It
  // also drives the correct font and hyphenation stack.
  //
  // Set here rather than in a component so it follows the language state
  // itself, and cannot be missed by a route that forgets to opt in.
  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = useCallback((key: string, options?: string | Record<string, string | number>): string => {
    let translation = translations[key];
    if (!translation) {
      if (typeof options === 'string') {
        return options; // Use default value
      }
      return key; // Fallback to key
    }
    if (typeof options === 'object' && options) {
        Object.keys(options).forEach(placeholder => {
            translation = translation.replace(`{${placeholder}}`, String(options[placeholder]));
        });
    }
    return translation;
  }, [translations]);

  const value = { language, setLanguage, t, loading };
  
  if (loading && !hasLoadedTranslations.current) {
      return (
        <div className="flex items-center justify-center min-h-screen bg-muted">
            <div className="text-primary font-semibold">Loading...</div>
        </div>
      );
  }

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};
